import { useState, useCallback } from "react";
import { toast } from "sonner";
import {
  usePerformanceDetailsLazyQuery,
  usePerformanceSummaryLazyQuery,
  useEvaluationsLazyQuery,
  Role,
  ClassificationType,
  AnalystPerformance,
  ReviewerPerformance,
  Evaluation,
} from "@/graphql/types/models";
import { exportPerformanceToExcel } from "@/lib/excelExport";
import {
  EXPORT_PERFORMANCE_LIMIT,
  EXPORT_EVALUATIONS_LIMIT,
} from "@/lib/constants";

const ERROR_CLASSIFICATIONS = [
  ClassificationType.NUMERIC_ERROR,
  ClassificationType.FORMATTING_ERROR,
  ClassificationType.MISSING_ERROR,
  ClassificationType.PARTIAL_EXTRACTION,
  ClassificationType.INCORRECT_EXTRACTION,
] as const;

export interface ExportFilters {
  readonly dateRange: { readonly from?: Date; readonly to?: Date };
  readonly personIds?: readonly string[];
  readonly classifications?: readonly ClassificationType[];
  readonly minDocuments?: number;
}

interface UseExcelExportOptions {
  readonly orgId: string;
}

interface UseExcelExportResult {
  readonly isExporting: boolean;
  readonly exportToExcel: (filters: ExportFilters) => Promise<void>;
}

function isAnalystPerformance(node: unknown): node is AnalystPerformance {
  return (
    typeof node === "object" &&
    node !== null &&
    "__typename" in node &&
    node.__typename === "AnalystPerformance"
  );
}

function isReviewerPerformance(node: unknown): node is ReviewerPerformance {
  return (
    typeof node === "object" &&
    node !== null &&
    "__typename" in node &&
    node.__typename === "ReviewerPerformance"
  );
}

function isEvaluation(node: unknown): node is Evaluation {
  return (
    typeof node === "object" &&
    node !== null &&
    "__typename" in node &&
    node.__typename === "Evaluation"
  );
}

export function useExcelExport({
  orgId,
}: UseExcelExportOptions): UseExcelExportResult {
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [fetchAnalystDetails] = usePerformanceDetailsLazyQuery();
  const [fetchReviewerDetails] = usePerformanceDetailsLazyQuery();
  const [fetchAnalystSummary] = usePerformanceSummaryLazyQuery();
  const [fetchReviewerSummary] = usePerformanceSummaryLazyQuery();
  const [fetchAnalystEvaluations] = useEvaluationsLazyQuery();
  const [fetchReviewerEvaluations] = useEvaluationsLazyQuery();

  const exportToExcel = useCallback(
    async (filters: ExportFilters): Promise<void> => {
      setIsExporting(true);

      try {
        const dateRangeFilter = {
          from: filters.dateRange.from?.toISOString(),
          to: filters.dateRange.to?.toISOString(),
        };

        const basePerformanceFilter = {
          orgId,
          dateRange: dateRangeFilter,
          personIds:
            filters.personIds && filters.personIds.length > 0
              ? [...filters.personIds]
              : undefined,
          classifications:
            filters.classifications && filters.classifications.length > 0
              ? [...filters.classifications]
              : undefined,
          minDocuments: filters.minDocuments,
        };

        const baseEvaluationFilter = {
          orgId,
          dateRange: dateRangeFilter,
          classifications: [...ERROR_CLASSIFICATIONS],
        };

        const [
          analystDetailsRes,
          reviewerDetailsRes,
          analystSummaryRes,
          reviewerSummaryRes,
          analystEvalsRes,
          reviewerEvalsRes,
        ] = await Promise.all([
          fetchAnalystDetails({
            variables: {
              filter: { ...basePerformanceFilter, role: Role.ANALYST },
              first: EXPORT_PERFORMANCE_LIMIT,
            },
            fetchPolicy: "network-only",
          }),
          fetchReviewerDetails({
            variables: {
              filter: { ...basePerformanceFilter, role: Role.REVIEWER },
              first: EXPORT_PERFORMANCE_LIMIT,
            },
            fetchPolicy: "network-only",
          }),
          fetchAnalystSummary({
            variables: {
              filter: { ...basePerformanceFilter, role: Role.ANALYST },
            },
            fetchPolicy: "network-only",
          }),
          fetchReviewerSummary({
            variables: {
              filter: { ...basePerformanceFilter, role: Role.REVIEWER },
            },
            fetchPolicy: "network-only",
          }),
          fetchAnalystEvaluations({
            variables: {
              filter: { ...baseEvaluationFilter, role: Role.ANALYST },
              first: EXPORT_EVALUATIONS_LIMIT,
            },
            fetchPolicy: "network-only",
          }),
          fetchReviewerEvaluations({
            variables: {
              filter: { ...baseEvaluationFilter, role: Role.REVIEWER },
              first: EXPORT_EVALUATIONS_LIMIT,
            },
            fetchPolicy: "network-only",
          }),
        ]);

        const analystNodes =
          analystDetailsRes.data?.performanceDetails?.edges?.map(
            (e) => e.node,
          ) ?? [];
        const reviewerNodes =
          reviewerDetailsRes.data?.performanceDetails?.edges?.map(
            (e) => e.node,
          ) ?? [];
        const analystEvalNodes =
          analystEvalsRes.data?.evaluations?.edges?.map((e) => e.node) ?? [];
        const reviewerEvalNodes =
          reviewerEvalsRes.data?.evaluations?.edges?.map((e) => e.node) ?? [];

        const analysts = analystNodes.filter(isAnalystPerformance);
        const reviewers = reviewerNodes.filter(isReviewerPerformance);
        const analystEvaluations = analystEvalNodes.filter(isEvaluation);
        const reviewerEvaluations = reviewerEvalNodes.filter(isEvaluation);

        if (
          analysts.length === 0 &&
          reviewers.length === 0 &&
          analystEvaluations.length === 0 &&
          reviewerEvaluations.length === 0
        ) {
          toast.warning("No data to export", {
            description: "There is no data available for the selected filters.",
          });
          return;
        }

        exportPerformanceToExcel({
          analysts,
          reviewers,
          analystEvaluations,
          reviewerEvaluations,
          analystSummary: analystSummaryRes.data?.performanceSummary,
          reviewerSummary: reviewerSummaryRes.data?.performanceSummary,
          dateRange: filters.dateRange,
        });

        const totalRecords =
          analysts.length +
          reviewers.length +
          analystEvaluations.length +
          reviewerEvaluations.length;

        toast.success("Export complete", {
          description: `Exported ${totalRecords.toLocaleString()} records to Excel.`,
        });
      } catch (error) {
        console.error("Export failed:", error);
        toast.error("Export failed", {
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while exporting.",
        });
      } finally {
        setIsExporting(false);
      }
    },
    [
      orgId,
      fetchAnalystDetails,
      fetchReviewerDetails,
      fetchAnalystSummary,
      fetchReviewerSummary,
      fetchAnalystEvaluations,
      fetchReviewerEvaluations,
    ],
  );

  return {
    isExporting,
    exportToExcel,
  } as const;
}
